import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Database, LayoutDashboard, Smartphone } from "lucide-react";

/**
 * Self-playing scene for the core bento cover.
 * A tiny API console fires individual endpoint calls; each success sends a data
 * pulse to the consumer app that taps that service, lighting its capability chip.
 */

interface Service {
  method: "POST" | "GET";
  path: string;
  short: string;
  chip: string;
  consumer: number;
}

const SERVICES: Service[] = [
  { method: "POST", path: "/v1/transcribe", short: "/transcribe", chip: "Transcription", consumer: 0 },
  { method: "POST", path: "/v1/soap", short: "/soap", chip: "SOAP Notes", consumer: 1 },
  { method: "POST", path: "/v1/coding", short: "/coding", chip: "Billing Codes", consumer: 2 },
  { method: "GET", path: "/v1/tracking", short: "/tracking", chip: "ROM Tracking", consumer: 0 },
];

const CONSUMERS = [
  { name: "Clinic App", Icon: Smartphone },
  { name: "Dashboard", Icon: LayoutDashboard },
  { name: "Partner EMR", Icon: Database },
];

const STEP_MS = 1150;
const LOOP_GAP_MS = 3000;

export default function CoreCoverWidget() {
  const [step, setStep] = useState(0);
  const timeoutRef = useRef<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const consumerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [line, setLine] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);

  const last = SERVICES.length - 1;
  const active = SERVICES[step];

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setStep(last);
      return;
    }
    timeoutRef.current = window.setTimeout(
      () => setStep((s) => (s >= last ? 0 : s + 1)),
      step >= last ? LOOP_GAP_MS : STEP_MS
    );
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [step, last]);

  useLayoutEffect(() => {
    const compute = () => {
      const c = containerRef.current;
      const r = rowRefs.current[step];
      const t = consumerRefs.current[active.consumer];
      if (!c || !r || !t) return;
      const cr = c.getBoundingClientRect();
      const rr = r.getBoundingClientRect();
      const tr = t.getBoundingClientRect();
      setLine({
        x1: rr.right - cr.left,
        y1: rr.top + rr.height / 2 - cr.top,
        x2: tr.left - cr.left,
        y2: tr.top + tr.height / 2 - cr.top,
      });
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [step, active.consumer]);

  const chipLit = (svcIdx: number) => svcIdx <= step;

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 p-2.5 sm:p-4 md:p-5 flex items-stretch gap-2 sm:gap-3 md:gap-4 pointer-events-none"
    >
      {line && (
        <svg className="absolute inset-0 w-full h-full z-20 overflow-visible pointer-events-none">
          <motion.path
            key={step}
            d={`M ${line.x1} ${line.y1} C ${(line.x1 + line.x2) / 2} ${line.y1}, ${(line.x1 + line.x2) / 2} ${line.y2}, ${line.x2} ${line.y2}`}
            fill="none"
            stroke="#7DD3FC"
            strokeWidth={2}
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          />
          <circle cx={line.x1} cy={line.y1} r={3} fill="#7DD3FC" />
          <motion.circle
            key={`dot-${step}`}
            r={3.5}
            fill="#7DD3FC"
            initial={{ cx: line.x1, cy: line.y1, opacity: 0 }}
            animate={{ cx: line.x2, cy: line.y2, opacity: [0, 1, 1, 0] }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          />
        </svg>
      )}

      {/* API console — wider on small screens so paths stay readable */}
      <div className="relative z-10 w-[54%] sm:w-[48%] shrink-0 rounded-xl sm:rounded-2xl bg-[#12302e] shadow-[0_16px_44px_-18px_rgba(0,0,0,0.6)] overflow-hidden flex flex-col">
        <div className="flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 border-b border-white/10">
          <span className="w-1.5 h-1.5 rounded-full bg-[#ef4444]/80" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#f5b942]/80" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#4ade80]/80" />
          <span className="ml-1 text-[8px] sm:text-[9px] font-mono text-white/60 truncate">
            core.fownd.care
          </span>
        </div>
        <div className="flex-1 min-h-0 px-1.5 sm:px-3 py-2 sm:py-2.5 flex flex-col justify-center gap-1 sm:gap-1.5">
          {SERVICES.map((svc, i) => {
            const fired = i <= step;
            return (
              <div
                key={svc.path}
                ref={(el) => (rowRefs.current[i] = el)}
                className={`flex items-center gap-1 sm:gap-1.5 transition-opacity duration-300 ${
                  fired ? "opacity-100" : "opacity-35"
                }`}
              >
                <span
                  className={`shrink-0 text-[6px] sm:text-[7px] font-bold px-1 py-0.5 rounded ${
                    svc.method === "POST"
                      ? "bg-[#439CCB]/20 text-[#7DD3FC]"
                      : "bg-[#4ade80]/20 text-[#86efac]"
                  }`}
                >
                  {svc.method}
                </span>
                <span className="text-[8px] sm:text-[9px] font-mono text-white/85 flex-1 min-w-0">
                  <span className="sm:hidden">{svc.short}</span>
                  <span className="hidden sm:inline">{svc.path}</span>
                </span>
                <AnimatePresence mode="wait">
                  {fired ? (
                    <motion.span
                      key="ok"
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="shrink-0 flex items-center gap-0.5 text-[6px] sm:text-[7px] font-bold text-[#4ade80]"
                    >
                      <span className="hidden sm:inline">200</span>
                      <Check className="w-2.5 h-2.5" />
                    </motion.span>
                  ) : (
                    <span key="dots" className="shrink-0 text-[8px] sm:text-[9px] text-white/30">
                      ···
                    </span>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>

      {/* Consumer tiles */}
      <div className="relative z-10 flex-1 min-w-0 flex flex-col justify-between gap-1.5 sm:gap-2">
        {CONSUMERS.map((c, ci) => {
          const chips = SERVICES.map((s, si) => ({ ...s, si })).filter(
            (s) => s.consumer === ci
          );
          const anyLit = chips.some((s) => chipLit(s.si));
          const isTarget = active.consumer === ci;
          const C = c.Icon;
          return (
            <div
              key={c.name}
              ref={(el) => (consumerRefs.current[ci] = el)}
              className={`flex-1 min-h-0 rounded-xl sm:rounded-2xl bg-white/95 px-2 sm:px-2.5 py-1.5 sm:py-2 flex flex-col justify-center transition-all duration-300 ${
                isTarget
                  ? "shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)] ring-1 ring-[#7DD3FC]/60"
                  : "shadow-[0_6px_20px_-12px_rgba(0,0,0,0.45)]"
              }`}
            >
              <div className="flex items-center gap-1 sm:gap-1.5 min-w-0">
                <span
                  className={`shrink-0 flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 rounded-md sm:rounded-lg transition-colors duration-300 ${
                    anyLit ? "bg-[#495B25]/12 text-[#495B25]" : "bg-gray-100 text-gray-400"
                  }`}
                >
                  <C className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </span>
                <span className="text-[8px] sm:text-[10px] font-bold font-productSans text-[#1F3D3B] truncate">
                  {c.name}
                </span>
              </div>
              <div className="mt-0.5 sm:mt-1 flex flex-wrap gap-0.5 sm:gap-1">
                {chips.map((s) => {
                  const lit = chipLit(s.si);
                  return (
                    <span
                      key={s.chip}
                      className={`text-[6px] sm:text-[7px] font-medium px-1 sm:px-1.5 py-0.5 rounded-full transition-colors duration-300 ${
                        lit ? "bg-[#486633] text-white" : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {s.chip}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
